import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminListMedia, adminUpdateMedia, adminUploadFile, adminAssignable, adminAssignImage, adminWarmImages, adminBackfillMedia,
} from "@/lib/api";
import {
  UploadCloud, Loader2, X, Trash2, Link2, FileText, Images, Search, Check, Save, Music, Zap, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { normalizeSrc, absMedia, imgSrc } from "@/lib/images";

// Render images with a same-origin path (works on any domain); use the absolute
// variant only for links / "copy URL".
const abs = (u) => normalizeSrc(u);
const absLink = (u) => absMedia(u);
const MAX_BATCH = 10;

/* ── Assign-to-content panel (image OR audio) ────────────────────── */
function AssignPanel({ mediaUrl, mode = "image", onClose }) {
  const isAudio = mode === "audio";
  const [q, setQ] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [done, setDone] = useState({});
  const { data, isLoading } = useQuery({
    queryKey: ["assignable", q],
    queryFn: () => adminAssignable(q),
    staleTime: 15_000,
  });
  // Audio recitations attach only to content items (kathas/mantras/stotras).
  const items = (data?.items || []).filter((it) => !isAudio || it.collection === "content");

  const assign = async (it) => {
    const k = `${it.collection}:${it.key}`;
    setBusyKey(k);
    try {
      const payload = isAudio
        ? { collection: it.collection, key: it.key, field: "audio", audio_url: mediaUrl }
        : { collection: it.collection, key: it.key, field: "image", image_url: mediaUrl };
      await adminAssignImage(payload);
      setDone((d) => ({ ...d, [k]: true }));
      toast.success(`Assigned to “${it.title}”`);
    } catch (e) {
      toast.error("Assign failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="assign-panel">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden border border-line bg-surface" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-line p-4">
          {isAudio ? <Music className="h-5 w-5 text-saffron" /> : <Link2 className="h-5 w-5 text-saffron" />}
          <h3 className="font-serif-latin text-lg text-ink">{isAudio ? "Assign audio to content" : "Assign image to content"}</h3>
          <button onClick={onClose} className="ml-auto text-ink-3 hover:text-ink" data-testid="assign-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex items-center gap-3 border-b border-line p-4">
          {isAudio ? (
            <audio src={abs(mediaUrl)} controls className="h-10 flex-1" data-testid="assign-audio-preview" />
          ) : (
            <img src={imgSrc(mediaUrl, 200)} alt="" className="h-14 w-14 shrink-0 border border-line object-cover" />
          )}
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={isAudio ? "Search Kathas, Mantras, Stotras…" : "Search Kathas, Mantras, Stotras, Temples, Devatas, Patrika…"} data-testid="assign-search" className="w-full border border-line bg-parchment py-2 pl-9 pr-3 text-sm text-ink focus:border-saffron focus:outline-none" />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <p className="py-16 text-center text-ink-3">Searching…</p>
          ) : items.length === 0 ? (
            <p className="py-16 text-center text-ink-3">No matching content.</p>
          ) : (
            <ul className="divide-y divide-line">
              {items.map((it) => {
                const k = `${it.collection}:${it.key}`;
                return (
                  <li key={k} className="flex items-center gap-3 px-2 py-2.5" data-testid="assign-item">
                    <div className="h-9 w-9 shrink-0 overflow-hidden border border-line bg-parchment">
                      {it.image ? <img src={imgSrc(it.image, 120)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-ink-3"><Images className="h-4 w-4" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-ink">{it.title || it.slug}</p>
                      <p className="text-xs text-ink-3">{it.type}</p>
                    </div>
                    <button
                      onClick={() => assign(it)}
                      disabled={busyKey === k}
                      data-testid="assign-apply"
                      className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs transition-colors ${done[k] ? "border-green-600 text-green-700" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"} disabled:opacity-60`}
                    >
                      {busyKey === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : done[k] ? <Check className="h-3.5 w-3.5" /> : null}
                      {done[k] ? "Assigned" : "Assign"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Edit / detail drawer for a single media item ────────────────── */
function MediaDrawer({ item, onClose, onChanged }) {
  const [title, setTitle] = useState(item.title || "");
  const [alt, setAlt] = useState(item.alt || "");
  const [tags, setTags] = useState((item.tags || []).join(", "));
  const [saving, setSaving] = useState(false);
  const [assign, setAssign] = useState(false);
  const isDoc = item.kind === "document";
  const isAudio = item.kind === "audio";

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateMedia(item.id, {
        title, alt,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Saved");
      onChanged();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Remove this from the library? Pages already using it keep working.")) return;
    try {
      await adminUpdateMedia(item.id, { delete: true });
      toast.success("Removed from library");
      onChanged();
      onClose();
    } catch (e) {
      toast.error("Delete failed", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end bg-black/50" onClick={onClose} data-testid="media-drawer">
      <div className="flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-line bg-surface" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-line p-4">
          <h3 className="font-serif-latin text-lg text-ink">{isDoc ? "Document" : isAudio ? "Audio" : "Image"} details</h3>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" data-testid="drawer-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4">
          {isDoc ? (
            <a href={absLink(item.url)} target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-line bg-parchment p-4 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
              <FileText className="h-6 w-6" /> Open document
            </a>
          ) : isAudio ? (
            <div className="border border-line bg-parchment p-4">
              <div className="mb-3 flex items-center gap-2 text-sm text-ink-2"><Music className="h-5 w-5 text-saffron" /> {item.original_filename || "Recitation"}</div>
              <audio src={abs(item.url)} controls className="w-full" data-testid="drawer-audio" />
            </div>
          ) : (
            <div className="overflow-hidden border border-line bg-parchment">
              <img src={imgSrc(item.url, 900)} alt={alt} className="max-h-72 w-full object-contain" data-testid="drawer-image" />
            </div>
          )}
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Caption / title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="drawer-title" className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
            </div>
            {!isDoc && !isAudio && (
              <div>
                <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Alt text (accessibility / SEO)</label>
                <input value={alt} onChange={(e) => setAlt(e.target.value)} data-testid="drawer-alt" className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Tags (comma separated)</label>
              <input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="drawer-tags" className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
            </div>
            <div className="flex items-center gap-2 border border-line bg-parchment px-3 py-2 text-xs text-ink-3">
              <Link2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{absLink(item.url)}</span>
              <button onClick={() => { navigator.clipboard?.writeText(absLink(item.url)); toast("URL copied"); }} className="ml-auto shrink-0 text-saffron hover:underline">Copy</button>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={save} disabled={saving} data-testid="drawer-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2 text-sm text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
            {!isDoc && (
              <button onClick={() => setAssign(true)} data-testid="drawer-assign" className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                {isAudio ? <Music className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} Assign to content
              </button>
            )}
            <button onClick={del} data-testid="drawer-delete" className="ml-auto inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-3 transition-colors hover:border-red-500 hover:text-red-600">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        </div>
      </div>
      {assign && <AssignPanel mediaUrl={item.url} mode={isAudio ? "audio" : "image"} onClose={() => setAssign(false)} />}
    </div>
  );
}

/* ── Media Library page ──────────────────────────────────────────── */
export default function AdminMedia() {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [tab, setTab] = useState("image");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(0);

  const { data, isLoading } = useQuery({ queryKey: ["admin-media"], queryFn: adminListMedia, staleTime: 10_000 });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-media"] });

  const all = data?.items || [];
  const images = all.filter((i) => i.kind === "image");
  const audios = all.filter((i) => i.kind === "audio");
  const docs = all.filter((i) => i.kind === "document");
  const source = tab === "image" ? images : tab === "audio" ? audios : docs;
  const list = source.filter((i) =>
    !q || (i.title || "").toLowerCase().includes(q.toLowerCase())
    || (i.original_filename || "").toLowerCase().includes(q.toLowerCase())
    || (i.tags || []).join(" ").toLowerCase().includes(q.toLowerCase()));

  const UP = {
    image: { accept: "image/*", prefix: "image/", cap: 12, label: "images" },
    audio: { accept: "audio/*", prefix: "audio/", cap: 40, label: "audio files" },
  };

  const [warming, setWarming] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const syncAll = async () => {
    setSyncing(true);
    try {
      const { added } = await adminBackfillMedia();
      refresh();
      toast.success(added ? `Synced ${added} new image${added === 1 ? "" : "s"} into the library.` : "All images are already in the library.");
    } catch {
      toast.error("Could not sync images.");
    } finally {
      setSyncing(false);
    }
  };
  const warmCache = async () => {
    setWarming(true);
    try {
      await adminWarmImages();
      toast.success("Image cache warming started — cards will load faster in a minute.");
    } catch {
      toast.error("Could not start image cache warm.");
    } finally {
      setWarming(false);
    }
  };

  const onFiles = async (e) => {
    const cfg = UP[tab] || UP.image;
    const files = Array.from(e.target.files || []).slice(0, MAX_BATCH);
    e.target.value = "";
    if (!files.length) return;
    if ((e.target.files?.length || 0) > MAX_BATCH) toast.warning(`Uploading the first ${MAX_BATCH} ${cfg.label} (max per batch).`);
    let ok = 0, fail = 0;
    setUploading(files.length);
    for (const f of files) {
      if (!f.type.startsWith(cfg.prefix)) { toast.error(`${f.name} is not a supported ${tab} file`); fail++; setUploading((n) => n - 1); continue; }
      if (f.size > cfg.cap * 1024 * 1024) { toast.error(`${f.name} is over ${cfg.cap} MB`); fail++; setUploading((n) => n - 1); continue; }
      try { await adminUploadFile(f); ok++; } catch { fail++; }
      setUploading((n) => n - 1);
    }
    setUploading(0);
    refresh();
    if (ok) toast.success(`Uploaded ${ok} ${cfg.label}${fail ? `, ${fail} failed` : ""}`);
    else if (fail) toast.error("Upload failed");
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">Media Library</h1>
          <p className="mt-1 text-sm text-ink-3">Upload images & audio recitations, then edit, tag, assign to any content, or remove.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={syncAll} disabled={syncing} data-testid="media-sync-all" title="Register every generated/banner image used across the site into this library" className="inline-flex items-center gap-2 border border-line px-4 py-2.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {syncing ? "Syncing…" : "Sync all images"}
          </button>
          <button onClick={warmCache} disabled={warming} data-testid="media-warm-cache" title="Pre-build optimized thumbnails so cards load instantly on mobile" className="inline-flex items-center gap-2 border border-line px-4 py-2.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
            {warming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {warming ? "Warming…" : "Warm image cache"}
          </button>
          {tab !== "document" && (
            <button onClick={() => inputRef.current?.click()} disabled={uploading > 0} data-testid="media-bulk-upload" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2.5 text-sm text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
              {uploading > 0 ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              {uploading > 0 ? `Uploading ${uploading}…` : tab === "audio" ? `Upload audio (up to ${MAX_BATCH})` : `Upload images (up to ${MAX_BATCH})`}
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept={(UP[tab] || UP.image).accept} multiple onChange={onFiles} className="hidden" data-testid="media-file-input" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 border-b border-line pb-3">
        <button onClick={() => setTab("image")} data-testid="media-tab-images" className={`px-3 py-1.5 text-sm ${tab === "image" ? "border-b-2 border-saffron text-saffron" : "text-ink-2 hover:text-ink"}`}>
          <span className="inline-flex items-center gap-1.5"><Images className="h-4 w-4" /> Images <span className="text-ink-3">({images.length})</span></span>
        </button>
        <button onClick={() => setTab("audio")} data-testid="media-tab-audio" className={`px-3 py-1.5 text-sm ${tab === "audio" ? "border-b-2 border-saffron text-saffron" : "text-ink-2 hover:text-ink"}`}>
          <span className="inline-flex items-center gap-1.5"><Music className="h-4 w-4" /> Audio <span className="text-ink-3">({audios.length})</span></span>
        </button>
        <button onClick={() => setTab("document")} data-testid="media-tab-docs" className={`px-3 py-1.5 text-sm ${tab === "document" ? "border-b-2 border-saffron text-saffron" : "text-ink-2 hover:text-ink"}`}>
          <span className="inline-flex items-center gap-1.5"><FileText className="h-4 w-4" /> Documents <span className="text-ink-3">({docs.length})</span></span>
        </button>
        <div className="relative ml-auto w-64 max-w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or tag…" data-testid="media-search" className="w-full border border-line bg-parchment py-2 pl-9 pr-3 text-sm text-ink focus:border-saffron focus:outline-none" />
        </div>
      </div>

      {isLoading ? (
        <p className="py-20 text-center text-ink-3">Loading…</p>
      ) : list.length === 0 ? (
        <p className="py-20 text-center text-ink-3">Nothing here yet.</p>
      ) : tab === "image" ? (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {list.map((i) => (
            <button key={i.id} type="button" onClick={() => setSelected(i)} data-testid="media-item" className="group relative aspect-square overflow-hidden border border-line bg-parchment hover:border-saffron">
              <img
                src={imgSrc(i.url, 360)}
                alt={i.alt || i.title || ""}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  const w = e.currentTarget.parentElement?.querySelector("[data-media-missing]");
                  e.currentTarget.style.display = "none";
                  if (w) w.style.display = "flex";
                }}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
              <span data-media-missing style={{ display: "none" }} className="absolute inset-0 flex-col items-center justify-center gap-1 bg-[var(--surface-alt)] text-center text-[10px] text-ink-3">
                <FileText className="h-5 w-5 text-ink-3/50" />
                File missing
              </span>
              {i.title && <span className="absolute inset-x-0 bottom-0 truncate bg-black/45 px-2 py-1 text-left text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100">{i.title}</span>}
            </button>
          ))}
        </div>
      ) : tab === "audio" ? (
        <ul className="mt-5 divide-y divide-line border border-line" data-testid="media-audio-list">
          {list.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-3 p-3" data-testid="media-audio-item">
              <Music className="h-5 w-5 shrink-0 text-saffron" />
              <span className="min-w-0 flex-1 truncate text-sm text-ink">{i.title || i.original_filename || "Recitation"}</span>
              <audio src={abs(i.url)} controls preload="none" className="h-9 w-full max-w-xs sm:w-64" />
              <button onClick={() => setSelected(i)} data-testid="audio-edit" className="shrink-0 text-xs text-ink-2 hover:text-saffron">Edit / Assign</button>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="mt-5 divide-y divide-line border border-line">
          {list.map((i) => (
            <li key={i.id} className="flex items-center gap-3 p-3" data-testid="media-doc-item">
              <FileText className="h-5 w-5 shrink-0 text-saffron" />
              <span className="min-w-0 flex-1 truncate text-sm text-ink">{i.title || i.original_filename}</span>
              <a href={absLink(i.url)} target="_blank" rel="noreferrer" className="shrink-0 text-xs text-saffron hover:underline">Open</a>
              <button onClick={() => setSelected(i)} className="shrink-0 text-xs text-ink-2 hover:text-ink">Edit</button>
            </li>
          ))}
        </ul>
      )}

      {selected && <MediaDrawer item={selected} onClose={() => setSelected(null)} onChanged={refresh} />}
    </div>
  );
}
